import * as React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableArrayItem } from "./SortableArrayItem";

/**
 * Wraps a list of pre-rendered array-item rows in a drag-and-drop sortable
 * context. Owns the sensors and drag-end handling; the parent supplies the
 * rows plus reorder/remove callbacks so this stays agnostic to item shape.
 * @param props - Dnd id, item ids, rendered rows, reorder/remove callbacks, and control flag
 * @returns The rendered sortable array field
 */
export function SortableArrayField({
  dndId,
  ids,
  rows,
  onReorder,
  onRemove,
  showControls,
}: {
  dndId: string;
  ids: string[];
  rows: React.ReactNode[];
  onReorder: (oldIndex: number, newIndex: number) => void;
  onRemove: (index: number) => void;
  showControls: boolean;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!showControls) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    onReorder(oldIndex, newIndex);
  };

  return (
    <DndContext
      id={dndId}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {rows.map((row, index) => (
            <SortableArrayItem
              key={ids[index]}
              id={ids[index]}
              onRemove={() => onRemove(index)}
              showControls={showControls}
            >
              {row}
            </SortableArrayItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
